import { useState, useEffect } from 'react';
import { api } from '../api';
import toast from 'react-hot-toast';
import NavIcon from '../components/NavIcon';

const FREQUENCIES = [
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

function hourLabel(h) {
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
}

export default function Export() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      const data = await api.exportSettings();
      setSettings(data);
    } catch {
      toast.error('Failed to load export settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateExportSettings({
        recipientEmail: settings.recipientEmail,
        scheduleEnabled: settings.scheduleEnabled,
        frequency: settings.frequency,
        scheduleHour: settings.scheduleHour,
      });
      setSettings(updated);
      toast.success('Export settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const exportNow = async () => {
    setExporting(true);
    try {
      const { blob, emailError } = await api.runExportNow();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storebook-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      if (emailError) {
        toast.error(`Downloaded, but email failed: ${emailError}`);
      } else if (settings.recipientEmail) {
        toast.success(`Downloaded and emailed to ${settings.recipientEmail}`);
      } else {
        toast.success('Downloaded. Add a recipient email to also send it by email.');
      }
      load(); // refresh last-run info
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading || !settings) {
    return <div className="page"><p className="text-muted">Loading…</p></div>;
  }

  return (
    <div className="page">
      <div className="page-header flex-between">
        <div>
          <h2>Data Export</h2>
          <p className="page-sub">Export your Categories, Suppliers, Products, Sales, and Purchases — same format as Bulk Import</p>
        </div>
      </div>

      {/* Export now */}
      <div className="card mb-6">
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NavIcon name="bulkimport" size={14} /> Export Now
        </div>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 14 }}>
          Generates an Excel file right now — downloads it to this device, and emails it to your
          saved recipient below (if one is set).
        </p>
        <button className="btn-primary" onClick={exportNow} disabled={exporting}>
          {exporting ? 'Exporting…' : 'Export Now'}
        </button>

        {settings.lastRunAt && (
          <p className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
            Last export: {new Date(settings.lastRunAt).toLocaleString()} —{' '}
            <span style={{ color: settings.lastRunStatus === 'success' ? 'var(--accent2)' : 'var(--danger)' }}>
              {settings.lastRunStatus === 'success' ? 'Success' : settings.lastRunStatus}
            </span>
          </p>
        )}
      </div>

      {/* Recipient + schedule */}
      <div className="card">
        <div className="section-title">Recipient & Schedule</div>

        <div className="form-row">
          <div className="field">
            <label>Recipient Email</label>
            <input
              type="email"
              placeholder="owner@example.com"
              value={settings.recipientEmail || ''}
              onChange={e => setSettings({ ...settings, recipientEmail: e.target.value })}
            />
          </div>
        </div>

        <div className="theme-toggle-row" style={{ marginTop: 6 }}>
          <span className="theme-toggle-label">
            <span>Automatic scheduled export</span>
          </span>
          <label className="theme-switch">
            <input
              type="checkbox"
              checked={settings.scheduleEnabled}
              onChange={e => setSettings({ ...settings, scheduleEnabled: e.target.checked })}
            />
            <span className="theme-switch-slider" />
          </label>
        </div>

        {settings.scheduleEnabled && (
          <div className="form-row" style={{ marginTop: 10 }}>
            <div className="field">
              <label>Frequency</label>
              <select
                value={settings.frequency}
                onChange={e => setSettings({ ...settings, frequency: e.target.value })}
              >
                {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Time of day</label>
              <select
                value={settings.scheduleHour}
                onChange={e => setSettings({ ...settings, scheduleHour: Number(e.target.value) })}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{hourLabel(h)}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <button className="btn-primary" onClick={save} disabled={saving} style={{ marginTop: 12 }}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
